'use client';

import { useEffect, useRef, useState, ReactNode, memo } from 'react';

interface BlurTextProps {
  children: ReactNode;
  delay?: number;
  duration?: number;
  className?: string;
  as?: 'h1' | 'h2' | 'h3' | 'p' | 'span' | 'div';
}

/**
 * BlurText - Text reveal animation with blur-to-focus effect
 */
function BlurTextComponent({
  children,
  delay = 0,
  duration = 0.8,
  className = '',
  as: Component = 'div',
}: BlurTextProps) {
  const ref = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    
    if (prefersReducedMotion) {
      setIsVisible(true);
      return;
    }

    const timer = setTimeout(() => setIsVisible(true), delay * 1000);
    return () => clearTimeout(timer);
  }, [delay]);

  const style = {
    opacity: isVisible ? 1 : 0,
    filter: isVisible ? 'blur(0px)' : 'blur(10px)',
    transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
    transition: `opacity ${duration}s ease-out, filter ${duration}s ease-out, transform ${duration}s ease-out`,
  };

  return (
    <Component
      ref={ref as any}
      className={className}
      style={style}
    >
      {children}
    </Component>
  );
}

export const BlurText = memo(BlurTextComponent);
